import type { BaseTokenInfo, TokenInfo, TokenValue } from "../../../types/base"
import { getDefuseAssetId, getTokenAid } from "../../../utils/token"
import {
  adjustDecimals,
  compareAmounts,
  deduplicateTokens,
  getUnderlyingBaseTokenInfos,
} from "../../../utils/tokenUtils"
import type { Holding } from "../../account/types/sharedTypes"
import {
  type TokenPrices,
  getHoldingFromBalances,
  getHoldingsFromBalances,
} from "../../account/utils/holdingsUtils"
import type { BalanceMapping } from "../../machines/depositedBalanceMachine"
import { getDestinationUsdValue, tokenHasSingleBaseUsd } from "./paymentFunding"

export type { TokenPrices } from "../../account/utils/holdingsUtils"

const MIN_USD_FLOOR = 1

export type PaymentSource = {
  displayToken: TokenInfo
  quoteToken: BaseTokenInfo
  spendableBalance: TokenValue
  usdValue: number
}

export function isSamePaymentFamily(
  paymentToken: TokenInfo,
  tokenIn: TokenInfo
): boolean {
  const paymentAid = getTokenAid(paymentToken)
  const tokenInAid = getTokenAid(tokenIn)
  if (paymentAid != null && tokenInAid != null) {
    return paymentAid === tokenInAid
  }
  return getDefuseAssetId(paymentToken) === getDefuseAssetId(tokenIn)
}

export function resolvePaymentSource(params: {
  paymentToken: TokenInfo
  balances: BalanceMapping
  tokenPrices?: TokenPrices
  siblingCandidates?: BaseTokenInfo[]
}): PaymentSource | null {
  const variants = params.siblingCandidates?.length
    ? deduplicateTokens(params.siblingCandidates)
    : getUnderlyingBaseTokenInfos(params.paymentToken)
  if (variants.length === 0) return null

  let selected: BaseTokenInfo | null = null
  let selectedComparableBalance = 0n
  const maxDecimals = Math.max(...variants.map((v) => v.decimals))

  for (const candidate of variants) {
    const balance = params.balances[candidate.defuseAssetId] ?? 0n
    const comparableBalance = adjustDecimals(
      balance,
      candidate.decimals,
      maxDecimals
    )

    if (selected == null || comparableBalance > selectedComparableBalance) {
      selected = candidate
      selectedComparableBalance = comparableBalance
    }
  }

  if (selected == null) return null

  return {
    displayToken: params.paymentToken,
    quoteToken: selected,
    spendableBalance: {
      amount: params.balances[selected.defuseAssetId] ?? 0n,
      decimals: selected.decimals,
    },
    usdValue:
      getHoldingFromBalances(
        selected,
        params.balances,
        params.tokenPrices ?? {}
      ).usdValue ?? 0,
  }
}

function holdingIsHeld(holding: Holding): boolean {
  return holding.value != null && holding.value.amount > 0n
}

function rankedTokensFromHoldings(holdings: Holding[]): TokenInfo[] {
  return holdings
    .filter(holdingIsHeld)
    .filter((holding) => (holding.usdValue ?? 0) >= MIN_USD_FLOOR)
    .sort((a, b) => (b.usdValue ?? 0) - (a.usdValue ?? 0))
    .map((holding) => holding.token)
}

export function getRankedHeldTokens(params: {
  tokens: TokenInfo[]
  balances: BalanceMapping
  tokenPrices: TokenPrices
}): TokenInfo[] {
  return rankedTokensFromHoldings(
    getHoldingsFromBalances(params.tokens, params.balances, params.tokenPrices)
  )
}

export function canTokenFundDestinationAmount(params: {
  paymentToken: TokenInfo
  destinationToken: TokenInfo
  requestedAmount: TokenValue
  balances: BalanceMapping
  tokenPrices: TokenPrices
}): boolean {
  const paymentSource = resolvePaymentSource({
    paymentToken: params.paymentToken,
    balances: params.balances,
    tokenPrices: params.tokenPrices,
  })
  if (paymentSource == null) return false

  if (isSamePaymentFamily(params.paymentToken, params.destinationToken)) {
    return (
      compareAmounts(paymentSource.spendableBalance, params.requestedAmount) >=
      0
    )
  }

  const requiredUsd = getDestinationUsdValue({
    destinationToken: params.destinationToken,
    requestedAmount: params.requestedAmount,
    tokenPrices: params.tokenPrices,
  })
  if (requiredUsd == null) return false

  return tokenHasSingleBaseUsd({
    token: params.paymentToken,
    balances: params.balances,
    tokenPrices: params.tokenPrices,
    requiredUsd,
  })
}

export function pickBaseTokenForPayment(
  paymentToken: TokenInfo,
  balances: BalanceMapping
): BaseTokenInfo | null {
  return resolvePaymentSource({ paymentToken, balances })?.quoteToken ?? null
}

export function pickDefaultPaymentToken(params: {
  destinationToken: TokenInfo
  heldTokens: TokenInfo[]
  balances: BalanceMapping
  tokenPrices: TokenPrices
}): TokenInfo | null {
  if (
    holdingIsHeld(
      getHoldingFromBalances(
        params.destinationToken,
        params.balances,
        params.tokenPrices
      )
    )
  ) {
    return params.destinationToken
  }

  const ranked = getRankedHeldTokens({
    tokens: params.heldTokens,
    balances: params.balances,
    tokenPrices: params.tokenPrices,
  })
  return ranked[0] ?? null
}

export function hasAlternativePaymentTokens(params: {
  heldTokens: TokenInfo[]
  balances: BalanceMapping
  tokenPrices: TokenPrices
}): boolean {
  const ranked = getRankedHeldTokens({
    tokens: params.heldTokens,
    balances: params.balances,
    tokenPrices: params.tokenPrices,
  })
  return ranked.length > 0
}
